// frontend/src/pages/asset-admin/AssetAdminExternalPersonnel.js
/**
 * Asset Admin – External / Outsourced Personnel
 *
 * Register and manage outsourced asset holders (e.g. the JB office) who are
 * NOT company employees: not on payroll, not in headcount, and with NO portal
 * login. They exist purely so IT custodians can assign/track company assets,
 * and they acknowledge receipt via an emailed one-time link.
 *
 * Theme: Red (Admin)
 * @see frontend/src/styles/design-system-reference.md
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Users, Plus, UserMinus, Search, Mail, Building2, Package,
  Send, X, Loader2, Globe, Briefcase,
} from 'lucide-react';
import toast from '../../utils/toast';
import Modal from '../../components/UI/Modal';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import ConfirmModal from '../../components/UI/ConfirmModal';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  useExternalPersonnel,
  useExternalWorkSites,
  useRegisterExternalPersonnel,
  useOffboardExternalPersonnel,
  useResendReceiptLink,
} from '../../hooks/useExternalPersonnel';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const getInitials = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const StatusBadge = ({ status }) => {
  const active = status === 'active';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
        active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {active ? 'Active' : 'Offboarded'}
    </span>
  );
};

// ─────────────────────────────────────────────
// REGISTER MODAL
// ─────────────────────────────────────────────

const RegisterModal = ({ isOpen, onClose }) => {
  const isMobile = useIsMobile();
  const [fullName, setFullName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [officeWorkSiteId, setOfficeWorkSiteId] = useState('');
  const [notes, setNotes] = useState('');

  const { data: workSites = [], isLoading: loadingSites } = useExternalWorkSites();
  const register = useRegisterExternalPersonnel();

  const reset = useCallback(() => {
    setFullName('');
    setWorkEmail('');
    setOfficeWorkSiteId('');
    setNotes('');
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !workEmail.trim()) {
      toast.error('Name and work email are required.');
      return;
    }
    try {
      await register.mutateAsync({
        full_name: fullName.trim(),
        work_email: workEmail.trim(),
        office_work_site_id: officeWorkSiteId ? Number(officeWorkSiteId) : null,
        notes: notes.trim(),
      });
      handleClose();
    } catch {
      /* error toast handled by the mutation */
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      theme="admin"
      customHeader={
        <div className="modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Globe className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="modal-title truncate">Register Outsourced Holder</h3>
          </div>
          <button type="button" onClick={handleClose} className="modal-close-admin" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      }
      footer={
        <>
          <button type="button" onClick={handleClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="register-external-form"
            disabled={register.isPending}
            className="btn btn-admin-primary"
          >
            {register.isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registering…</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" />Register Holder</>
            )}
          </button>
        </>
      }
    >
      <form id="register-external-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          This person is <strong>not</strong> a company employee — no payroll, no
          headcount, no portal login. They confirm asset receipt through a secure
          link emailed to the address below. Do not store any PDPA-sensitive data.
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="e.g. Ahmad bin Ismail"
            className="input-admin w-full"
            autoFocus={!isMobile}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Work email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="email"
              value={workEmail}
              onChange={(e) => setWorkEmail(e.target.value)}
              placeholder="name@company.com"
              className="input-admin pl-10 w-full"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">Receipt links are sent here.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Office / work site
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={officeWorkSiteId}
              onChange={(e) => setOfficeWorkSiteId(e.target.value)}
              className="input-admin pl-10 w-full"
              disabled={loadingSites}
            >
              <option value="">{loadingSites ? 'Loading sites…' : '— Select office (optional) —'}</option>
              {workSites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.site_name}{site.project_code ? ` (${site.project_code})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional, non-sensitive notes"
            className="input-admin w-full resize-none"
          />
        </div>
      </form>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// HOLDER CARD
// ─────────────────────────────────────────────

const HolderCard = ({ holder, onOffboard, onResend, resending }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-semibold text-purple-600">{getInitials(holder.full_name)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{holder.full_name}</p>
        <p className="text-xs text-gray-500 font-mono">{holder.staff_id}</p>
      </div>
      <StatusBadge status={holder.employment_status} />
    </div>

    <div className="space-y-1.5 text-xs text-gray-600 flex-1">
      <p className="flex items-center gap-1.5 truncate">
        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="truncate">{holder.work_email}</span>
      </p>
      <p className="flex items-center gap-1.5 truncate">
        <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="truncate">{holder.office_work_site_name || '—'}</span>
      </p>
      {(holder.designation || holder.department_name) && (
        <p className="flex items-center gap-1.5 truncate">
          <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {[holder.designation, holder.department_name].filter(Boolean).join(' · ')}
          </span>
        </p>
      )}
      <p className="flex items-center gap-1.5">
        <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span>{holder.held_assets_count} asset(s) currently held</span>
      </p>
    </div>

    <div className="card-action-footer btn-row-spread">
      <button
        onClick={() => onResend(holder)}
        disabled={resending}
        className="btn btn-sm btn-admin-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
        title="Re-send the asset receipt link for any pending assets"
      >
        {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Resend link
      </button>
      {holder.employment_status === 'active' && (
        <button
          onClick={() => onOffboard(holder)}
          className="btn btn-sm btn-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
        >
          <UserMinus className="w-3.5 h-3.5" />
          Offboard
        </button>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

const AssetAdminExternalPersonnel = () => {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [offboardTarget, setOffboardTarget] = useState(null);

  const { data: holders = [], isLoading } = useExternalPersonnel();
  const offboard = useOffboardExternalPersonnel();
  const resendLink = useResendReceiptLink();

  const filtered = useMemo(() => {
    if (!search.trim()) return holders;
    const q = search.toLowerCase();
    return holders.filter(
      (h) =>
        (h.full_name || '').toLowerCase().includes(q) ||
        (h.staff_id || '').toLowerCase().includes(q) ||
        (h.work_email || '').toLowerCase().includes(q) ||
        (h.office_work_site_name || '').toLowerCase().includes(q)
    );
  }, [holders, search]);

  const handleOffboard = async () => {
    if (!offboardTarget) return;
    try {
      await offboard.mutateAsync(offboardTarget.staff_id);
      setOffboardTarget(null);
    } catch {
      /* error toast handled by the mutation */
    }
  };

  const handleResend = (holder) => resendLink.mutate(holder.staff_id);

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">External / Outsourced Personnel</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Non-employee asset holders (e.g. JB office). No login — they confirm receipt by email.
            </p>
          </div>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn btn-admin-primary w-full sm:w-auto flex-shrink-0">
          <Plus className="w-4 h-4" />
          Register Holder
        </button>
      </div>

      {/* Toolbar */}
      <DebouncedSearchInput
        value={search}
        onSearch={setSearch}
        placeholder="Search by name, ID, email or office…"
        className="max-w-xl"
      />

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 animate-pulse h-44" />
          ))}
        </div>
      ) : filtered.length === 0 && !search ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No outsourced holders yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Register a holder to assign and track their company assets
          </p>
          <button className="btn btn-admin-primary mt-4" onClick={() => setModalOpen(true)}>
            <Plus className="w-4 h-4" />
            Register Holder
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No holders match &ldquo;{search}&rdquo;</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((h) => (
            <HolderCard
              key={h.staff_id}
              holder={h}
              onOffboard={setOffboardTarget}
              onResend={handleResend}
              resending={resendLink.isPending && resendLink.variables === h.staff_id}
            />
          ))}
        </div>
      )}

      {/* Register modal */}
      <RegisterModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Offboard confirmation */}
      <ConfirmModal
        isOpen={!!offboardTarget}
        title="Offboard Holder"
        message={`Offboard ${offboardTarget?.full_name}? They will no longer appear in the assignment picker. Their asset history is preserved. Recover any assets still held first.`}
        confirmLabel="Offboard"
        variant="danger"
        theme="admin"
        isLoading={offboard.isPending}
        onConfirm={handleOffboard}
        onCancel={() => setOffboardTarget(null)}
      />
    </div>
  );
};

export default AssetAdminExternalPersonnel;
